'use client';

import Icon from '@/components/ui/AppIcon';
import { ViewMode } from './CalendarInteractive';

interface CalendarHeaderProps {
  viewMode: ViewMode;
  currentDate: Date;
  onViewChange: (mode: ViewMode) => void;
  onNavigate: (direction: 'prev' | 'next' | 'today') => void;
}

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function getWeekRange(date: Date): string {
  const d = new Date(date);
  const day = d.getDay();
  const start = new Date(d);
  start.setDate(d.getDate() - day);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return `${MONTHS[start.getMonth()]} ${start.getDate()} – ${start.getMonth() !== end.getMonth() ? MONTHS[end.getMonth()] + ' ' : ''}${end.getDate()}, ${end.getFullYear()}`;
}

export default function CalendarHeader({ viewMode, currentDate, onViewChange, onNavigate }: CalendarHeaderProps) {
  const getTitle = () => {
    if (viewMode === 'month') return `${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    if (viewMode === 'week') return getWeekRange(currentDate);
    return `${DAYS[currentDate.getDay()]}, ${MONTHS[currentDate.getMonth()]} ${currentDate.getDate()}, ${currentDate.getFullYear()}`;
  };

  return (
    <div className="flex items-center justify-between px-4 py-3 bg-card border-b border-border flex-shrink-0">
      <div className="flex items-center gap-3">
        <button
          onClick={() => onNavigate('today')}
          className="px-3 py-1.5 text-sm font-medium font-body bg-muted hover:bg-border text-foreground rounded-md transition-standard"
          suppressHydrationWarning
        >
          Today
        </button>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onNavigate('prev')}
            className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-standard"
            aria-label="Previous"
            suppressHydrationWarning
          >
            <Icon name="ChevronLeftIcon" size={18} />
          </button>
          <button
            onClick={() => onNavigate('next')}
            className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-standard"
            aria-label="Next"
            suppressHydrationWarning
          >
            <Icon name="ChevronRightIcon" size={18} />
          </button>
        </div>
        <h2 className="font-heading font-semibold text-foreground text-lg">{getTitle()}</h2>
      </div>

      <div className="flex items-center gap-2">
        <div className="flex items-center bg-muted rounded-md p-0.5">
          {(['month', 'week', 'day'] as ViewMode[]).map(mode => (
            <button
              key={mode}
              onClick={() => onViewChange(mode)}
              className={`px-3 py-1.5 text-sm font-medium font-body rounded-md capitalize transition-standard ${
                viewMode === mode ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
              suppressHydrationWarning
            >
              {mode}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}